import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api, { formatError } from "@/lib/api";
import { linkUser as linkPushUser, logoutUser as logoutPushUser } from "@/lib/push";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = checking, false = unauth, object = auth
    const [error, setError] = useState("");
    const [impersonatedBy, setImpersonatedBy] = useState(() => {
        try { return JSON.parse(localStorage.getItem("gtw_impersonated_by") || "null"); }
        catch { return null; }
    });

    const fetchMe = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            if (data?.id) linkPushUser(data.id);
        } catch (e) {
            console.debug("auth/me failed (treating as unauth):", e?.response?.status);
            setUser(false);
        }
    }, []);

    useEffect(() => {
        fetchMe();
    }, [fetchMe]);

    // Per-user UI preferences (ticketStatusFilter:<uid>) live in
    // localStorage and persist across relogins — no login-time purge.

    const login = async (identifier, password) => {
        setError("");
        try {
            const { data } = await api.post("/auth/login", { identifier, password });
            // Admin / Staff logins now go through an email-OTP step. Bubble
            // the OTP challenge back to the Login page so it can render the
            // step-2 code input; do NOT set the user session yet.
            if (data?.otp_required) return { otp_required: true, otp_token: data.otp_token, masked_email: data.masked_email, role: data.role };
            if (data?.access_token) localStorage.setItem("gtw_token", data.access_token);
            // Clear any leftover impersonation state when logging in fresh
            localStorage.removeItem("gtw_admin_token");
            localStorage.removeItem("gtw_impersonated_by");
            // Per-user UI preferences (ticketStatusFilter:<uid>) are
            // intentionally kept across relogins — see PRD 2026-02-22.
            setImpersonatedBy(null);
            setUser(data.user);
            if (data.user?.id) linkPushUser(data.user.id);
            return data.user;
        } catch (e) {
            const msg = formatError(e, "Login failed");
            setError(msg);
            throw new Error(msg);
        }
    };

    // ── OTP step (admin + staff email OTP flow) ──────────────────────
    const verifyOtp = async (otpToken, code) => {
        setError("");
        try {
            const { data } = await api.post("/auth/verify-otp", { otp_token: otpToken, code });
            if (data?.access_token) localStorage.setItem("gtw_token", data.access_token);
            localStorage.removeItem("gtw_admin_token");
            localStorage.removeItem("gtw_impersonated_by");
            // Session-scoped UI prefs — the user asked for the ticket status
            // filter (and similar view choices) to persist across navigation
            // but reset on next OTP login (Aug 2026). Wipe keys namespaced to
            // the freshly-authenticated user id so the next session starts
            // with the app's defaults.
            if (data?.user?.id) {
                try {
                    localStorage.removeItem(`ticketStatusFilter:${data.user.id}`);
                } catch { /* ignore */ }
            }
            setImpersonatedBy(null);
            setUser(data.user);
            if (data.user?.id) linkPushUser(data.user.id);
            return data.user;
        } catch (e) {
            const msg = formatError(e, "OTP verification failed");
            setError(msg);
            throw new Error(msg);
        }
    };

    const resendOtp = async (otpToken) => {
        setError("");
        const { data } = await api.post("/auth/resend-otp", { otp_token: otpToken, code: "" });
        return data;
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch (e) {
            console.error("logout failed:", e);
        }
        localStorage.removeItem("gtw_token");
        localStorage.removeItem("gtw_admin_token");
        localStorage.removeItem("gtw_impersonated_by");
        setImpersonatedBy(null);
        setUser(false);
        logoutPushUser();
    };

    // ---- Preview As (impersonation) ----
    const startPreviewAs = useCallback(async (targetUserId) => {
        const { data } = await api.post(`/auth/impersonate/${targetUserId}`);
        // Stash the current admin token so we can restore later
        const currentToken = localStorage.getItem("gtw_token");
        if (currentToken) localStorage.setItem("gtw_admin_token", currentToken);
        localStorage.setItem("gtw_token", data.access_token);
        localStorage.setItem("gtw_impersonated_by", JSON.stringify(data.impersonated_by));
        setImpersonatedBy(data.impersonated_by);
        setUser(data.user);
        return data.user;
    }, []);

    const stopPreviewAs = useCallback(async () => {
        const adminToken = localStorage.getItem("gtw_admin_token");
        if (adminToken) {
            localStorage.setItem("gtw_token", adminToken);
            localStorage.removeItem("gtw_admin_token");
        }
        localStorage.removeItem("gtw_impersonated_by");
        setImpersonatedBy(null);
        await fetchMe();
    }, [fetchMe]);

    // ---- Family switch (client-to-client, consent-gated) ----
    // Uses the same "stash → swap → restore" pattern as Preview-As, but
    // routed through /portal/family-switch which enforces shared family
    // membership + opt-in flag.
    const familySwitchInto = useCallback(async (targetUserId) => {
        const { data } = await api.post("/portal/family-switch", { target_user_id: targetUserId });
        const currentToken = localStorage.getItem("gtw_token");
        if (currentToken) localStorage.setItem("gtw_admin_token", currentToken);  // re-use the same stash slot
        localStorage.setItem("gtw_token", data.access_token);
        localStorage.setItem("gtw_impersonated_by", JSON.stringify({
            id: data.switched_from, kind: "family", family_id: data.family_id, family_name: data.family_name,
        }));
        setImpersonatedBy({
            id: data.switched_from, kind: "family", family_id: data.family_id, family_name: data.family_name,
        });
        setUser(data.user);
        return data.user;
    }, []);

    const refreshUser = fetchMe;

    // Stable context value — avoids re-rendering every consumer on every parent render.
    const value = useMemo(
        () => ({
            user, login, logout, error, setError, refreshUser,
            impersonatedBy, startPreviewAs, stopPreviewAs, familySwitchInto,
            verifyOtp, resendOtp,
        }),
        [user, error, refreshUser, impersonatedBy, startPreviewAs, stopPreviewAs, familySwitchInto],
    );

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
